import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Tabs, Tab, Paper, Card, CardContent } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import BusinessSettings from '../components/business/BusinessSettings';
import BusinessSettingsGuide from '../components/onboarding/BusinessSettingsGuide';
import { useOnboarding, OnboardingStep } from '../contexts/OnboardingContext';
import BusinessRolesTab from 'components/business/BusinessRolesTab';
import BusinessUsersTab from 'components/business/BusinessUsersTab';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';

const BusinessSettingsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const { user, isAuthenticated } = useAuth();
    const hasAccess = user?.isAdmin || user?.businessRoles?.some(role => role.isBusinessAdmin);
    const location = useLocation();
    const navigate = useNavigate();
    const { isOnboarding, currentStep } = useOnboarding();
    const { theme } = useBusinessTheme();

    // Parse query parameters when component mounts or URL changes
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tabParam = queryParams.get('tab');
        
        if (tabParam) {
            switch (tabParam) {
                case 'roles':
                    setActiveTab(1); // Index for Roles tab
                    break;
                case 'users':
                    setActiveTab(2); // Index for Users tab
                    break;
                case 'appointment-types':
                    setActiveTab(3); // Index for Appointment Types tab
                    break;
                default:
                    setActiveTab(0); // Default to main settings tab
            }
        }
    }, [location.search]);

    // Update URL when tab changes
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        
        // Update URL to reflect tab change
        let queryParam = '';
        switch (newValue) {
            case 1:
                queryParam = 'roles';
                break;
            case 2:
                queryParam = 'users';
                break;
            case 3:
                queryParam = 'appointment-types';
                break;
            default:
                queryParam = '';
        }
        
        // Update URL without full page reload
        navigate(
            queryParam ? `/business-settings?tab=${queryParam}` : '/business-settings',
            { replace: true }
        );
    };

    // Highlight the current tab if it's the active onboarding step
    const getTabHighlight = (tabIndex: number) => {
        let currentOnboardingStepMatch = false;
        
        if (isOnboarding) {
            switch (tabIndex) {
                case 0:
                    currentOnboardingStepMatch = currentStep === OnboardingStep.BusinessSettings;
                    break;
                case 1:
                    currentOnboardingStepMatch = currentStep === OnboardingStep.Roles;
                    break;
                case 2:
                    currentOnboardingStepMatch = currentStep === OnboardingStep.Users;
                    break;
                case 3:
                    currentOnboardingStepMatch = currentStep === OnboardingStep.AppointmentTypes;
                    break;
            }
        }
        
        return currentOnboardingStepMatch ? {
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 1
        } : {};
    };

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    if (!hasAccess) {
        return <Navigate to="/dashboard" />;
    }

    // Render the appropriate content based on active tab
    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return <BusinessSettings showOnlyGeneralTab={true} />;
            case 1:
                return <BusinessRolesTab businessId={user?.businessId || ''} />;
            case 2:
                return <BusinessUsersTab businessId={user?.businessId || ''} />;
            case 3:
                return '';//<AppointmentTypesManagement />;
            default:
                return <BusinessSettings showOnlyGeneralTab={true} />;
        }
    };

    return (
        <>
            <Helmet>
                <title>Business Settings | MindShare Calendar</title>
            </Helmet>
            <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
                <Card sx={{ backgroundColor: theme.accentColor }}>
                    <CardContent>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Business Settings
                        </Typography>
                        
                        <BusinessSettings 
                            initialTab={activeTab}
                            onTabChange={(newTabIndex) => {
                                // Update the URL when the tab changes
                                const newTab = ['', 'roles', 'users', 'appointment-types'][newTabIndex];
                                if (newTab) {
                                    navigate(`/business-settings?tab=${newTab}`, { replace: true });
                                } else {
                                    navigate('/business-settings', { replace: true });
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </Container>
        </>
    );
};

export default BusinessSettingsPage;